import type { CollectionCodeSnippetOptions } from './types';
import { serializeJavascriptObject } from '~/utils/code-snippet/js-serialization/serialize';
import { formatJavascriptMemberExpression } from '~/utils/code-snippet/js-serialization/strings';
import { getDefaultResultVariableName } from './identifiers';
import { assertUnsupportedOperation } from './operation';
import { createSdkReadPayload, createSdkWritePayload, shouldReturnFields } from './sdk-payload';

/**
 * Generates a TypeScript SDK snippet for the requested collection operation.
 */
export function getCollectionSdkSnippet(options: CollectionCodeSnippetOptions): string {
	const { collection, operation } = options;
	const clientName = options.clientName ?? 'client';
	const collectionAccessor = options.collectionAccessor ?? collection.apiName;
	const resultVariableName = options.resultVariableName ?? getDefaultResultVariableName(collection, operation);
	const memberExpression = formatJavascriptMemberExpression(clientName, collectionAccessor);

	switch (operation) {
		case 'readFirst':
		case 'readMany': {
			const payload = serializeJavascriptObject(createSdkReadPayload(options));

			return `const ${resultVariableName} = await ${memberExpression}.${operation}(${payload});`;
		}
		case 'readOne': {
			const payload = serializeJavascriptObject(createSdkReadPayload(options));

			return `const ${resultVariableName} = await ${memberExpression}.readOne(${payload});`;
		}
		case 'createMany':
		case 'createOne':
		case 'updateMany':
		case 'updateOne': {
			const payload = serializeJavascriptObject(createSdkWritePayload(options));

			return `const ${resultVariableName} = await ${memberExpression}.${operation}(${payload});`;
		}
		case 'deleteMany':
		case 'deleteOne': {
			const payload = serializeJavascriptObject(createSdkWritePayload(options));

			if (shouldReturnFields(options)) {
				return `const ${resultVariableName} = await ${memberExpression}.${operation}(${payload});`;
			}

			return `await ${memberExpression}.${operation}(${payload});`;
		}
		default:
			return assertUnsupportedOperation(operation);
	}
}
